import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qcpSocketAuth } from '@/lib/qcp/auth';
import {
  ClientEvents,
  ServerEvents,
  type MmEnqueue,
  type MmMatchedPayload,
} from '@/lib/qcp/protocol';

/**
 * The two configured DEFAULT_BUCKETS on the QCP gameserver.
 *
 * CRITICAL: The server only pairs players within the SAME bucket. Sending any
 * other (bank, inc) pair enqueues the player but they will NEVER be matched.
 * This type is a discriminated union of the only two valid payloads the UI
 * may emit — a free-form clock picker is intentionally absent.
 */
export type CpQueueBucket =
  | { bank: 60; inc: 5 }    // "Fast"     — 1:00 bank + 0:05 increment
  | { bank: 120; inc: 10 }; // "Standard" — 2:00 bank + 0:10 increment

/** The two hardcoded preset values (one per allowed server bucket). */
export const CP_QUEUE_BUCKETS: [CpQueueBucket, CpQueueBucket] = [
  { bank: 60, inc: 5 },
  { bank: 120, inc: 10 },
];

export type CpQueueStatus = 'idle' | 'searching' | 'matched';

export interface CpQueueController {
  status: CpQueueStatus;
  /** ISO timestamp at which we emitted `mm:enqueue`, used for the elapsed clock. */
  enqueuedAt: number | null;
  /** Set once the server pairs us (`mm:matched`) — the caller navigates to the match. */
  matched: string | null;
  error: string | null;
  /**
   * Emit `mm:enqueue` with a configured bucket.
   * The bucket MUST be one of `CP_QUEUE_BUCKETS` — the type system enforces this.
   */
  enqueue: (bucket: MmEnqueue) => void;
  /** Disconnect the socket and reset to idle (Cancel button / backdrop tap). */
  leave: () => void;
}

/**
 * Drives the QCP ranked matchmaking queue from the TMA.
 *
 * Lifecycle: opens ONE Socket.IO connection to the QCP gameserver (same auth
 * as `useCpInvite` / `useQcpMatch`) on mount, then keeps it open while waiting
 * for a match.  The socket is purely a listener until `enqueue()` is called;
 * it does NOT join any match room.
 *
 * `enqueue(bucket)` emits `mm:enqueue` to the server with the chosen clock
 * preset. The server places the player in the queue for that bucket and, once
 * paired, emits `mm:matched { matchId }` back over the socket.  On receipt the
 * hook sets `status = 'matched'` and exposes the paired `matchId` via
 * `matched` — the caller (CpLobby) reacts to it and calls `startMatch` so the
 * new match is registered as the one active match before navigating.
 *
 * `leave()` disconnects the socket (the server self-dequeues on disconnect)
 * and resets state to `idle`.  On unmount the socket is always disconnected to
 * prevent leaks — `unmountedRef` guards the async `qcpSocketAuth()` call from
 * creating a socket after the component is gone.
 *
 * Clock presets are constrained to `CpQueueBucket` — only the two values that
 * match the server's `DEFAULT_BUCKETS` ({bank:60,inc:5} and {bank:120,inc:10})
 * are accepted by the type system. A non-bucket pair would enqueue but never
 * pair (the user waits forever), so the UI must never expose a free-form picker.
 */
export function useCpQueue(): CpQueueController {
  const socketRef = useRef<Socket | null>(null);
  // Desired bucket to (re)emit once the socket is connected. Set by enqueue();
  // flushed in the socket 'connect' handler so a tap made BEFORE the async
  // socket creation (qcpSocketAuth → network token refresh) isn't silently lost.
  const pendingRef = useRef<MmEnqueue | null>(null);
  const [status, setStatus] = useState<CpQueueStatus>('idle');
  const [enqueuedAt, setEnqueuedAt] = useState<number | null>(null);
  const [matched, setMatched] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mount-once: open the listen socket.  `mm:matched` arrives here after the
  // server pairs us; no `match:join` emit needed (we're not joining a room yet,
  // just registering for matchmaking events).
  useEffect(() => {
    // Per-effect cancellation flag — NOT a ref. React 18/19 StrictMode (dev)
    // mounts → unmounts → remounts; a shared `useRef` flag set true by the first
    // cleanup STAYED true through the remount, so the socket was never created
    // (queue dead on dev-tma: dbg stuck at "init", conn=false, nothing enqueued).
    // A local `let` is fresh per run — same pattern as useQcpMatch, which is why
    // vs-bot worked and the queue didn't.
    let cancelled = false;
    void qcpSocketAuth().then((auth) => {
      // Bail if this effect run was cleaned up before the async token resolved.
      if (cancelled) return;

      const socket = io(env.qcpWsUrl, {
        auth,
        transports: ['websocket', 'polling'],
        reconnection: true,
      });
      socketRef.current = socket;

      // Flush a pending enqueue once connected — covers the race where the user
      // taps a bucket BEFORE this socket finished its async auth/creation.
      socket.on('connect', () => {
        if (pendingRef.current) socket.emit(ClientEvents.MmEnqueue, pendingRef.current);
      });

      // Surface auth/transport failures instead of failing silently.
      socket.on('connect_error', (e: Error) => {
        setError(e.message || 'connection failed');
        setStatus('idle');
      });

      // Server-side matchmaking error (bad payload / unauthorized / mm_failed).
      socket.on(ServerEvents.Err, (p: { message?: string }) => {
        setError(p?.message ?? 'queue error');
        setStatus('idle');
      });

      socket.on(ServerEvents.MmMatched, (p: MmMatchedPayload) => {
        pendingRef.current = null;
        setStatus('matched');
        setMatched(p.matchId);
      });
    });

    return () => {
      cancelled = true;
      // Disconnect on unmount — the gameserver self-dequeues when the socket drops.
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
    // Mount-once: stable refs only; no reactive dependencies.
  }, []);

  const enqueue = useCallback((bucket: MmEnqueue) => {
    setError(null);
    setEnqueuedAt(Date.now());
    setMatched(null);
    setStatus('searching');
    // Remember the desired bucket so the 'connect' handler can (re)send it if
    // the socket isn't ready yet.
    pendingRef.current = bucket;
    // If already connected, emit now; otherwise the 'connect' handler flushes
    // pendingRef once the socket comes up.
    if (socketRef.current?.connected) {
      socketRef.current.emit(ClientEvents.MmEnqueue, bucket);
    }
  }, []);

  const leave = useCallback(() => {
    setStatus('idle');
    setEnqueuedAt(null);
    setMatched(null);
    setError(null);
    pendingRef.current = null;
    socketRef.current?.disconnect();
    socketRef.current = null;
  }, []);

  return { status, enqueuedAt, matched, error, enqueue, leave };
}
